// Logbook type registry -- the single source of truth for the different kinds
// of logbook a user can attach to an Item. Shared by the browser (list / detail
// / edit pages) and, in principle, the Lambdas.
//
// Each type declares:
//   id          - stable identifier persisted on TaskPogoLogbook.logbookType
//   defaultName - the name a freshly-created logbook of this type gets (the
//                 user can rename it, e.g. "Airframe" / "Engine" / "Propeller"
//                 for several Maintenance logs on one airplane)
//   fields      - entry field definitions, same shape the EditForm / detail
//                 renderers already use for tasks (`{ name, key, type, ... }`),
//                 extended with a `storage` marker:
//                   'column' -> stored in a dedicated TaskPogoCompletedTask
//                               attribute (name, date, counter, notes, ...)
//                   'json'   -> stored inside the free-form `fields` JSON blob
//                               so new logbook types need no schema change.
//                 and an optional `share` marker:
//                   share: false -> never published by a public share link.
//                               Absent means shared; see publicFieldsForType.
//   listColumns - the columns the logbook list table shows (subset of fields,
//                 by key).
//   titleColumns- (optional) field keys used to build a human title for an entry
//                 on types that have no Name field (Captain's / Pilot's). When
//                 present, entryTitle joins these values instead of reading the
//                 (meaningless) `name`, and entryHeading prefixes the date.
//   emptyHelp   - the copy shown when a logbook of this type has no entries:
//                 `{ what, how }`, one sentence each. It lives here rather than
//                 in the component because what a log RECORDS is the type's
//                 business -- a Captain's Log does not track completed tasks --
//                 and because `how` has to name the same second path the type's
//                 own gates already allow (supportsTaskCompletion for
//                 Maintenance; Copy to Captain's Log for Captain's). It names
//                 the TYPE, not the logbook's name: a renamed log ("Airframe")
//                 still has to say what kind of log it is. No "above"/"below" --
//                 LogbookSummary renders this under the Add button and
//                 Logbook.jsx renders it over one, so no direction is true in
//                 both.
//
// To add a new logbook type in the future: append an entry here. No schema
// change is required as long as the new columns use `storage: 'json'`.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import { completedTaskFields } from './CompletedTaskFields.mjs';
import { displayDate } from './Common.mjs';
import { resolvePlaceName } from './PlaceFields.mjs';
import {
  KEY_ENTRY,
  isNamedGroupKey,
  normalizeTripName,
  tripKeyFor,
} from './TripIdentity.mjs';
// An entry stores no distance -- summarizeLogbook MEASURES it from the entry's
// route, which is the rule this whole module exists to keep singular. Safe to
// import: TripRoute reaches only GeoBounds/PlaceFields and nothing there comes
// back here. LogbookTrips imports THIS module, so it must never be imported
// from here.
import { routeFromEntry, routeDistanceNm } from './TripRoute.mjs';

dayjs.extend(utc);

export const LOGBOOK_TYPE_MAINTENANCE = 'maintenance';
export const LOGBOOK_TYPE_CAPTAINS = 'captains';
export const LOGBOOK_TYPE_PILOTS = 'pilots';

// Maintenance Log: the original/legacy logbook. Every existing entry (dev/prod)
// is migrated into one of these, so its fields are exactly the historical
// CompletedTask columns -- all column-backed.
const maintenanceFields = completedTaskFields.map((f) => ({ ...f, storage: 'column' }));

// Captain's Log: trip-oriented. Trip Name leads (ahead of Date) so it's the
// first field on the entry detail page -- the natural thing to name/identify
// a trip by, and where initial focus lands when the page loads.
const captainsFields = [
  // `type: 'trip'` renders the typeahead over this logbook's existing trips and
  // `idKey` names the second key it owns -- the same pair TripFields gives the
  // legs' Trip Name. The id is what files an entry under a trip; the name is a
  // label, which is what lets four unrelated day cruises all be called "Day"
  // and still be four trips. See TripIdentity.mjs.
  { name: 'Trip Name', key: 'tripName', type: 'trip', idKey: 'tripGroupId', storage: 'json' },
  { name: 'Date', key: 'date', type: 'date', storage: 'column' },
  // `place` rather than `text`: these are gazetteer names (TaskPogoPlace), and
  // the entry detail page edits them through the route's Start/End rows. The
  // typeahead is what keeps the two surfaces naming the same row.
  { name: 'From', key: 'from', type: 'place', storage: 'json' },
  { name: 'To', key: 'to', type: 'place', storage: 'json' },
  // Who operated the boat. Free text; BLANK MEANS THE OWNER -- nothing is
  // stamped at create time, so every existing entry is already the owner's by
  // absence, and no name the app would have to guess at (an email is not a
  // display name) is ever written. `idKey` reserves `captainUserId` for a
  // future binding to a real account, the exact tripName/tripGroupId shape:
  // the id has no field entry of its own, so it stays out of fieldsForType
  // and LogbookIO's import whitelist strips it -- when a picker arrives it
  // writes both keys and nothing about the save path changes. Captains only
  // for now: Maintenance already has fixByEmail ("Done By"), and a Pilot's
  // PIC-name column is a different product question (FAA conventions) that
  // this registry makes a one-line addition later.
  { name: 'Captain', key: 'captain', type: 'text', storage: 'json', idKey: 'captainUserId' },
  // `share: false`: a counter is the ITEM's meter, not a fact about the trip.
  { name: 'Start Counter', key: 'startCounter', type: 'number', step: 'any', storage: 'json', share: false },
  { name: 'End Counter', key: 'endCounter', type: 'number', step: 'any', storage: 'json', share: false },
  { name: 'Hours', key: 'hours', type: 'number', step: 'any', storage: 'json' },
  // Rendered inline to the right of Hours (detail + edit); not its own row.
  // Missing/null means included so existing entries keep their totals.
  {
    name: 'Include in Summary',
    key: 'includeInSummary',
    type: 'boolean',
    storage: 'json',
    default: true,
    inlineWith: 'hours',
    // The owner's bookkeeping about their OWN totals, so it is not published.
    // The consequence is handled server-side: share-logbook-trip computes the
    // trip summary itself, because a reader without the flag would count an
    // excluded entry (isIncludedInSummary defaults to included).
    share: false,
  },
  // Notes is where the day actually gets written down, so it gets the room to
  // write it in. `textarea` shares text's save contract (see EditForm.jsx) --
  // it rides the debounce and flushes on the container's blur -- so nothing
  // about the autosave path changes. `storage` stays 'column': splitEntryFields
  // reads it to keep the notes off the `fields` blob.
  { name: 'Notes', key: 'notes', type: 'textarea', rows: 5, storage: 'column' },
];

// Pilot's Logbook: FAA-style flight log. Tail Number leads (ahead of Date),
// matching the same "identifying field first" pattern as Captain's Trip Name
// and Maintenance's Name.
const pilotsFields = [
  { name: 'Tail Number', key: 'tailNumber', type: 'text', storage: 'json' },
  { name: 'Date', key: 'date', type: 'date', storage: 'column' },
  { name: 'From', key: 'from', type: 'place', storage: 'json' },
  { name: 'To', key: 'to', type: 'place', storage: 'json' },
  // "Filed Route" rather than "Route", and it stays free text: this is the
  // route as filed with ATC ("KSEA V23 KPDX"), which sits on the same page as
  // the map route the waypoint list edits. Two fields called Route is a
  // collision the pilot meets, not just the code. The KEY stays `route` -- the
  // display name is all that changed, so no migration -- which is also why the
  // map route is stored at `geoRoute` (see TripRoute.mjs).
  { name: 'Filed Route', key: 'route', type: 'text', storage: 'json' },
  // Not shared, for the same reason as the Captain's pair above.
  { name: 'Start Counter', key: 'startCounter', type: 'number', step: 'any', storage: 'json', share: false },
  { name: 'End Counter', key: 'endCounter', type: 'number', step: 'any', storage: 'json', share: false },
  { name: 'PIC', key: 'pic', type: 'number', step: 'any', storage: 'json' },
  { name: 'Day Takeoffs', key: 'dayTakeoffs', type: 'number', storage: 'json' },
  { name: 'Day Landings', key: 'dayLandings', type: 'number', storage: 'json' },
  { name: 'Night Takeoffs', key: 'nightTakeoffs', type: 'number', storage: 'json' },
  { name: 'Night Landings', key: 'nightLandings', type: 'number', storage: 'json' },
  { name: 'Instrument', key: 'instrument', type: 'number', step: 'any', storage: 'json' },
  { name: 'Sim Instrument', key: 'simInstrument', type: 'number', step: 'any', storage: 'json' },
  { name: 'Holds', key: 'holds', type: 'number', storage: 'json' },
  { name: 'Fuel Remaining', key: 'fuelRemaining', type: 'number', step: 'any', storage: 'json' },
  // Multi-line, column-backed -- see captainsFields' Notes above.
  { name: 'Notes', key: 'notes', type: 'textarea', rows: 5, storage: 'column' },
];

export const LOGBOOK_TYPES = [
  {
    id: LOGBOOK_TYPE_MAINTENANCE,
    label: 'Maintenance Log',
    defaultName: 'Maintenance Log',
    // Maintenance entries can be created by completing a Task (which carries
    // was-due / interval / recurrence data). Other types cannot.
    supportsTaskCompletion: true,
    fields: maintenanceFields,
    listColumns: ['date', 'counter', 'name'],
    emptyHelp: {
      what: 'The Maintenance Log records work done on this item.',
      how: 'Add an entry with the Add button, or by completing a task.',
    },
  },
  {
    id: LOGBOOK_TYPE_CAPTAINS,
    label: "Captain's Log",
    defaultName: "Captain's Log",
    supportsTaskCompletion: false,
    // Entries carry a geo route (TripRoute.mjs, at fields.geoRoute): the
    // detail page shows the waypoint list and a map, and From/To are edited
    // through the route's Start/End rows rather than as their own inputs.
    supportsRoute: true,
    fields: captainsFields,
    // `captain` in the list: a blank cell reads naturally as "you", so only
    // third-party entries carry a name -- which is exactly the "entries for
    // me and for someone else" view the column exists for.
    listColumns: ['date', 'tripName', 'from', 'to', 'captain', 'hours'],
    titleColumns: ['from', 'to'],
    emptyHelp: {
      what: "The Captain's Log records the trips you've taken.",
      how: 'Add an entry with the Add button, or copy a leg from a planned trip.',
    },
    summary: { hoursKey: 'hours', countLabel: 'Trips', countMode: 'uniqueTripName', tripKey: 'tripName' },
    // Saving this type's End Counter may also move the item's own counter
    // (see src/utilities/itemCounterSync.mjs). Captain's only, deliberately:
    // a boat's single hour meter and the log's End Counter are the same
    // number, while a Pilot's tach/Hobbs value has no such claim on whatever
    // counter an aircraft item happens to carry.
    syncsItemCounter: true,
  },
  {
    id: LOGBOOK_TYPE_PILOTS,
    label: "Pilot's Logbook",
    defaultName: "Pilot's Logbook",
    supportsTaskCompletion: false,
    supportsRoute: true,
    fields: pilotsFields,
    listColumns: ['date', 'tailNumber', 'from', 'to'],
    titleColumns: ['from', 'to'],
    emptyHelp: {
      what: "The Pilot's Logbook records the flights you've flown.",
      how: 'Add an entry with the Add button.',
    },
    summary: { hoursKey: 'pic', countLabel: 'Flights', countMode: 'rows' },
  },
];

// The trip-oriented logbook types: a record of where the item actually went,
// as opposed to what was done to it. The cross-item Trips page shows these
// (alongside upcoming trips) and leaves Maintenance on the item page. Add any
// future travel-log type here rather than open-coding the ids at call sites.
export const TRIP_LOGBOOK_TYPES = [LOGBOOK_TYPE_CAPTAINS, LOGBOOK_TYPE_PILOTS];

export function isTripLogbookType(id) {
  return TRIP_LOGBOOK_TYPES.includes(id);
}

// Look up a type by id. Falls back to Maintenance for unknown / missing ids so
// legacy rows (and defensive callers) always render sensibly.
export function logbookTypeById(id) {
  return LOGBOOK_TYPES.find((t) => t.id === id) || LOGBOOK_TYPES[0];
}

export function fieldsForType(id) {
  return logbookTypeById(id).fields;
}

// The fields a PUBLIC share link may show. Two callers, and deliberately both:
// share-logbook-trip's handler, which decides what is put on the wire, and
// SharedLogbookEntry, which decides what is drawn. That is the doubled
// whitelist README describes -- one rule, enforced at both ends, so a payload
// that somehow carries a marked key still renders no row for it.
//
// OPT-OUT, not opt-in (`share !== false`). A share is a deliberate publication
// of a trip -- From/To, hours, captain, notes, photos and the map ARE the thing
// being sent -- so opt-in would need a marker on every field of every type just
// to keep today's behaviour, Maintenance's included (its fields are mapped in
// from CompletedTaskFields). The backstop for a field nobody thought about is
// not this function: it is the handler's SELECTION SET, which admits only
// id/date/notes/fields into the process at all.
//
// What is marked today, and why each one:
//   startCounter / endCounter -- the ITEM's engine meter, not the trip's.
//   includeInSummary          -- the owner's bookkeeping about their own totals.
//
// ONLY A `storage: 'json'` FIELD MAY BE MARKED, and a test enforces it. The two
// halves are not symmetric: the page half drops whatever this returns, but the
// WIRE half is `publicJsonKeys`, which reaches only the `fields` blob -- a
// column-backed field rides the handler's selection set instead and would be
// published regardless. Marking one would therefore hide a row while still
// sending its value, which is the worst of both. Withholding a column means
// narrowing that selection set, not adding a marker here.
export function publicFieldsForType(id) {
  return fieldsForType(id).filter((field) => field.share !== false);
}

export function defaultNameForType(id) {
  return logbookTypeById(id).defaultName;
}

export function supportsTaskCompletion(id) {
  return logbookTypeById(id).supportsTaskCompletion === true;
}

// The `{ what, how }` copy for an empty logbook of this type. Unknown / missing
// ids fall back to Maintenance's, the same way every other accessor here does.
export function emptyHelpForType(id) {
  return logbookTypeById(id).emptyHelp;
}

// Does this type's entries carry a geo route? Gates the waypoint list, the
// map, and the removal of the standalone From/To inputs on the detail page.
// Ask this rather than testing for a `from` field or hard-coding a type id --
// a future type could have From/To without a map, or the reverse.
export function supportsRoute(id) {
  return logbookTypeById(id).supportsRoute === true;
}

// Does saving this type's End Counter also move the item's own counter?
// Ask this rather than testing the type id -- turning the sync on for another
// type is a registry decision, not a call-site edit.
export function syncsItemCounter(id) {
  return logbookTypeById(id).syncsItemCounter === true;
}

// The list-table column definitions (resolved from `listColumns` keys to the
// full field objects) for a type.
export function listColumnsForType(id) {
  const type = logbookTypeById(id);
  return type.listColumns
    .map((key) => type.fields.find((f) => f.key === key))
    .filter(Boolean);
}

// A raw `fields` blob (string | object | null) -> plain object. AppSync
// AWSJSON round-trips as a string, but some code paths (and tests) hand back
// a plain object -- accept both, and never throw.
function parseFieldsBlob(raw) {
  if (raw == null) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) ?? {};
  } catch {
    return {};
  }
}

// Parse the `fields` JSON blob off an entry.
export function parseEntryFields(entry) {
  return parseFieldsBlob(entry ? entry.fields : null);
}

// Read a single field's value off an entry, transparently handling
// column-backed vs. json-backed storage.
export function readEntryField(entry, field, jsonCache) {
  if (!entry) return null;
  if (field.storage === 'json') {
    const json = jsonCache || parseEntryFields(entry);
    const v = json[field.key];
    return v === undefined ? null : v;
  }
  const v = entry[field.key];
  return v === undefined ? null : v;
}

// Whether an entry has any notes worth showing in a list. Reads the `notes`
// COLUMN (every type declares it column-backed), which is why this lives here
// beside readEntryField rather than on each page that lists entries.
export function entryHasNotes(entry) {
  return typeof entry?.notes === 'string' && entry.notes.trim() !== '';
}

// Join a type's fields (by key) off an entry into a " - "-separated string,
// skipping empty values and formatting dates. Shared by entryTitle/entryHeading.
function joinFields(entry, type, keys, json, placeIndex) {
  const parts = [];
  for (const key of keys) {
    const field = type.fields.find((f) => f.key === key);
    if (!field) continue;
    let v = readEntryField(entry, field, json);
    if (v == null || v === '') continue;
    if (field.type === 'date') v = displayDate(v);
    // A summary row is one line, so it gets the gazetteer's short name. With
    // no index -- every caller that doesn't ask for one -- this is a no-op and
    // the full name survives, which is what keeps dialogs and file names long.
    if (field.type === 'place') v = resolvePlaceName(v, placeIndex);
    parts.push(String(v));
  }
  return parts;
}

// A short human label for an entry, used in summary rows and delete confirms.
// Maintenance entries have a Name and use it. Typed logbooks (Captain's /
// Pilot's) have no Name, so they build a label from their `titleColumns`
// (e.g. "<From> - <To>"), falling back to a generic label. The date is NOT
// included here because summary rows already render it in their own column.
// `placeIndex` is optional and OFF by default: a caller that passes one gets
// the gazetteer's short names (a summary row), and a caller that doesn't gets
// exactly what this returned before (a delete confirmation, where the qualifier
// is what tells two similarly-named places apart before something is destroyed).
export function entryTitle(entry, typeId, { placeIndex } = {}) {
  if (!entry) return '';
  const type = logbookTypeById(typeId);
  const json = parseEntryFields(entry);

  if (type.titleColumns) {
    const parts = joinFields(entry, type, type.titleColumns, json, placeIndex);
    return parts.length ? parts.join(' - ') : 'Entry';
  }

  if (entry.name) return entry.name;
  // Legacy fallback for a typed logbook without titleColumns: first non-date
  // list column that has a value.
  for (const key of type.listColumns) {
    if (key === 'date') continue;
    const field = type.fields.find((f) => f.key === key);
    if (!field) continue;
    const v = readEntryField(entry, field, json);
    if (v != null && v !== '') return String(v);
  }
  return 'Entry';
}

// A fuller heading for an entry's own detail page. Maintenance keeps its Name;
// typed logbooks lead with the date (which the detail page has no other title
// for) followed by the title columns, e.g. "May 1, 2026 - Portland - Seattle".
export function entryHeading(entry, typeId, { placeIndex } = {}) {
  if (!entry) return '';
  const type = logbookTypeById(typeId);
  if (!type.titleColumns) {
    return entryTitle(entry, typeId, { placeIndex });
  }
  const json = parseEntryFields(entry);
  const parts = joinFields(entry, type, ['date', ...type.titleColumns], json, placeIndex);
  return parts.length ? parts.join(' - ') : 'Entry';
}

// Whether an entry's hours contribute to the logbook summary card. Defaults to
// included when the flag is missing so legacy rows keep their historical totals.
export function isIncludedInSummary(entry, jsonCache) {
  const json = jsonCache || parseEntryFields(entry);
  return json.includeInSummary !== false;
}

// The comparison key for a trip name: trimmed and lowercased, because the name
// is hand-typed on every entry and "Bay Tour" and "bay tour " are one name to
// everyone but a string comparison. '' for a blank name, which never groups --
// an entry with no trip name is not "the trip called nothing".
//
// One line rather than a second implementation: the legs' copy of this used to
// be a byte-identical trim-and-lowercase in TripGroups.mjs, and the two once
// disagreed on case. There is now one normalizer, in TripIdentity.mjs, and this
// is the name the logbook side has always called it by.
export const tripNameKey = normalizeTripName;

// The key of the trip an entry belongs to: 'g:<tripGroupId>' for an entry filed
// under a trip, 'n:<name>' for one written before ids existed, 'e:<entry id>'
// for an entry nobody filed at all.
//
// The id in the entry's `fields` blob is what actually files it. The name is a
// label, so two unrelated day cruises can both be called "Day" and still be two
// trips -- which is the whole reason this stopped being tripNameKey. See
// TripIdentity.mjs for the precedence and for why a nameless entry stands alone
// whatever id it carries.
//
// It lives here, beside summarizeLogbook, rather than in LogbookTrips.mjs
// (which re-exports it) for the reason tripNameKey did: the card's per-year
// TRIP COUNT and the per-year page's TRIP ROWS have to be the same rule, and the
// only way to guarantee that is for them to be the same code.
//
// `json` is the entry's already-parsed blob, for callers walking a long list --
// parseEntryFields on every entry twice over is the one cost worth avoiding
// here.
export function entryTripKey(entry, typeId, json) {
  const type = logbookTypeById(typeId);
  const tripKey = type.summary?.tripKey;
  // A type with no notion of a trip (Pilot's, Maintenance): every entry stands
  // alone, which is the only honest thing to say about it.
  if (!tripKey) return KEY_ENTRY + (entry?.id ?? '');
  const field = type.fields.find((f) => f.key === tripKey);
  const blob = json || parseEntryFields(entry);
  return tripKeyFor({
    name: field ? readEntryField(entry, field, blob) : null,
    tripGroupId: field?.idKey ? blob[field.idKey] : null,
    ownId: entry?.id,
    alonePrefix: KEY_ENTRY,
  });
}

// The calendar year an entry belongs to, or null when its date is missing or
// unparseable. UTC for the reason every date read in this file is: a date
// stored as UTC midnight would otherwise bucket a year early in the Americas.
//
// The blank check is load-bearing: `dayjs.utc(undefined)` is NOW, so a row
// with no date at all would otherwise be filed under the current year -- and
// on the per-year page it would appear as a trip with no dates that nobody
// logged.
export function entryYear(entry) {
  const raw = entry?.date;
  if (raw == null || raw === '') return null;
  const d = dayjs.utc(raw);
  return d.isValid() ? d.year() : null;
}

// Aggregate a whole logbook into totals + a per-year breakdown, for the summary
// card at the top of the Captain's / Pilot's pages. Returns null for types with
// no `summary` descriptor (e.g. Maintenance).
//
// Shape:
//
//   { totalHours, totalEntries, totalDistanceNm, totalCount, countLabel,
//     countIsEntries, byYear: [{ year, hours, entryCount, distanceNm, count }] }
//
// Years are newest-first, and the per-year breakdown only includes rows with a
// parseable `date`.
//
// Four rules, and the last one is the one that surprises people:
//
// 1. `hours` sums the type's hours field (Captain's `hours`, Pilot's `pic`).
//    `entryCount` is simply how many rows fell in the year. `count` is unique
//    trip KEYS ('uniqueTripName') or the row count ('rows'), per the type's
//    `countMode` -- and `countIsEntries` says which, so the summary card can
//    tell whether its count column IS the entry column rather than sniffing a
//    mode string it would then have to understand.
//
// 2. `distanceNm` is MEASURED from each entry's route, never read: an entry
//    stores no distance, on purpose, so there is no second copy here that
//    could drift from the map. `null` rather than 0 when nothing in the year
//    measured -- a year whose entries carry no routes says nothing about how
//    far it went, it didn't go nowhere. This is the one implementation of that
//    sum in the app; summarizeTripEntries delegates to it.
//
// 3. `includeInSummary` gates HOURS ONLY. Trip counts, entry counts and
//    distance all include an excluded row, so the trip itself stays in the
//    roster and its miles still happened. That is what summarizeTripEntries
//    has always done, and making distance follow the flag here would put the
//    card and the per-trip page into disagreement. Don't "fix" it.
//
// 4. `totalHours`, `totalEntries` and `totalDistanceNm` cover EVERY row,
//    including one whose date is missing or unparseable and which therefore
//    belongs to no year. So the footer can exceed the sum of its own column.
//    That is correct, not a rounding bug (see entriesForYear's comment). It is
//    also not a free choice: summarizeTripEntries' `count` is every row it was
//    handed, so a dated-only convention would silently drop an undated entry's
//    miles from the trip page. `totalCount` follows the same rule for a 'rows'
//    type, whose count column is the entry column; for 'uniqueTripName' it
//    stays the sum of the year rows, because a trip crossing New Year belongs
//    to both years and has no year of its own to be counted under.
export function summarizeLogbook(entries, typeId) {
  const type = logbookTypeById(typeId);
  if (!type.summary) return null;
  const { hoursKey, tripKey, countMode, countLabel } = type.summary;
  const hoursField = type.fields.find((f) => f.key === hoursKey);
  const tripField = tripKey ? type.fields.find((f) => f.key === tripKey) : null;
  // Deliberately a NEGATIVE test, and not a stylistic choice. It is the exact
  // expression `count` has always used (`countMode === 'uniqueTripName' ?
  // trips : rows`), just named -- so a countMode nobody has written yet still
  // counts rows, as it always did. The positive form (`=== 'rows'`) reads
  // safer and is worse: an unknown mode would fall to the trip branch, and
  // `trips` is only ever populated for a type declaring a `summary.tripKey`,
  // so such a type would report 0 trips for every year with nothing saying so.
  // A plausible number beats a silent zero.
  const countIsEntries = countMode !== 'uniqueTripName';

  let totalHours = 0;
  let totalEntries = 0;
  let totalDistanceNm = 0;
  let measured = false;
  const years = new Map(); // year -> { hours, trips:Set, rows, nm, measured }
  for (const entry of entries || []) {
    const json = parseEntryFields(entry);
    const h = hoursField ? Number(readEntryField(entry, hoursField, json)) : NaN;
    const includeHours = isIncludedInSummary(entry, json);
    if (Number.isFinite(h) && includeHours) totalHours += h;
    totalEntries += 1;

    // Measured, never stored. Read through the blob we already parsed:
    // routeFromEntry would otherwise JSON.parse the same string a second time,
    // once per entry, on a scan of the whole logbook.
    const nm = routeDistanceNm(routeFromEntry(entry, json));
    if (nm != null) {
      totalDistanceNm += nm;
      measured = true;
    }

    const y = entryYear(entry);
    if (y === null) continue; // still counted in the totals above
    if (!years.has(y)) {
      years.set(y, { hours: 0, trips: new Set(), rows: 0, nm: 0, measured: false });
    }
    const bucket = years.get(y);
    if (Number.isFinite(h) && includeHours) bucket.hours += h;
    bucket.rows += 1;
    if (nm != null) {
      bucket.nm += nm;
      bucket.measured = true;
    }
    if (tripField) {
      // The entry's TRIP key, not its typed spelling: a user who wrote "Bay
      // Tour" on one entry and "bay tour" on the next made one trip, and two
      // unrelated day cruises both called "Day" made two -- the per-trip page
      // believes both, and this count is the same rule so it cannot disagree.
      //
      // An unfiled entry keys as standing alone and is not counted, which is
      // what the blank-name check here has always done.
      const key = entryTripKey(entry, typeId, json);
      if (isNamedGroupKey(key)) bucket.trips.add(key);
    }
  }

  const byYear = [...years.entries()]
    .map(([year, b]) => ({
      year,
      hours: b.hours,
      entryCount: b.rows,
      distanceNm: b.measured ? b.nm : null,
      count: countIsEntries ? b.rows : b.trips.size,
    }))
    .sort((a, b) => b.year - a.year);

  return {
    totalHours,
    totalEntries,
    totalDistanceNm: measured ? totalDistanceNm : null,
    totalCount: countIsEntries ? totalEntries : byYear.reduce((s, r) => s + r.count, 0),
    countLabel,
    countIsEntries,
    byYear,
  };
}

// Fold several logbooks' summaries into one, for the fleet Trips page's
// Summary card. Takes summarizeLogbook results (nulls -- a log with no
// summary -- are skipped) and returns one of the same shape, or null when
// nothing is left to sum.
//
// A merge of SUMMARIES, deliberately not summarizeLogbook over the
// concatenated entries, for two reasons that both change the numbers:
//
// 1. Each log has its own TYPE. A Captain's hour is its `hours` field and a
//    Pilot's is `pic`; one typeId cannot read both.
// 2. A trip key is unique only within a logbook. Two boats' logs can each
//    carry a legacy 'n:day', and those are two trips -- one page, one share
//    row, one calendar bar each -- where a concatenated set would count one.
//
// So every number is a sum of per-log answers and nothing is re-derived:
//
// - hours, entry counts and counts add, per year and in total.
// - distance is null-preserving: a year (or a fleet) is null only when every
//   log's answer was, which keeps the card's "no Distance column" gate meaning
//   what it means on one log. A log that measured nothing adds nothing, it
//   doesn't add zero.
// - countIsEntries is true only when EVERY log's is (an all-Pilot's fleet).
//   A mixed fleet's count is trips plus flights, which is not its entry
//   count, so the card draws both columns.
// - countLabel is the one label when the logs agree and the distinct labels
//   joined with ' / ' when they don't ("Trips / Flights"), in the order met.
//
// Years come back newest first, the card's contract.
export function mergeLogbookSummaries(summaries) {
  const present = (summaries || []).filter(Boolean);
  if (present.length === 0) return null;

  let totalHours = 0;
  let totalEntries = 0;
  let totalCount = 0;
  let totalDistanceNm = null;
  let countIsEntries = true;
  const labels = [];
  const years = new Map(); // year -> { hours, entryCount, distanceNm, count }

  for (const s of present) {
    totalHours += s.totalHours;
    totalEntries += s.totalEntries;
    totalCount += s.totalCount;
    if (s.totalDistanceNm != null) totalDistanceNm = (totalDistanceNm ?? 0) + s.totalDistanceNm;
    countIsEntries = countIsEntries && Boolean(s.countIsEntries);
    if (!labels.includes(s.countLabel)) labels.push(s.countLabel);

    for (const row of s.byYear || []) {
      if (!years.has(row.year)) {
        years.set(row.year, { hours: 0, entryCount: 0, distanceNm: null, count: 0 });
      }
      const bucket = years.get(row.year);
      bucket.hours += row.hours;
      bucket.entryCount += row.entryCount;
      bucket.count += row.count;
      if (row.distanceNm != null) bucket.distanceNm = (bucket.distanceNm ?? 0) + row.distanceNm;
    }
  }

  const byYear = [...years.entries()]
    .map(([year, b]) => ({ year, ...b }))
    .sort((a, b) => b.year - a.year);

  return {
    totalHours,
    totalEntries,
    totalDistanceNm,
    totalCount,
    countLabel: labels.join(' / '),
    countIsEntries,
    byYear,
  };
}

// Produce a flat object (all field keys at the top level) suitable for editing
// with the generic EditForm, hoisting json-backed values out of `entry.fields`.
// Non-field scalars already on the entry (id, itemId, logbookId, taskId, ...)
// are preserved so downstream save handlers can read them.
export function flattenEntry(entry, typeId) {
  const type = logbookTypeById(typeId);
  const flat = { ...entry };
  delete flat.fields;
  const json = parseEntryFields(entry);
  for (const field of type.fields) {
    if (field.storage === 'json') {
      if (json[field.key] !== undefined) {
        flat[field.key] = json[field.key];
      } else if (field.default !== undefined) {
        flat[field.key] = field.default;
      } else {
        flat[field.key] = null;
      }
    }
    // An idKey field owns a second key -- the id binding the entry to a trip
    // (tripGroupId) or, later, a captain to an account (captainUserId) --
    // with no field entry of its own, exactly as an interval field owns
    // `monthsKey`. Hoisting it here is what lets the Trip Name typeahead read
    // the current binding, and what lets splitEntryFields write a changed one
    // back; without it an unregistered id could be preserved forever but never
    // CHANGED, which is the whole gesture.
    if (field.idKey && field.storage === 'json') {
      flat[field.idKey] = json[field.idKey] ?? null;
    }
  }
  return flat;
}

// Start Counter / End Counter / Hours describe the same run of the engine (or
// airframe), so keep them consistent while the user edits: changing either
// counter recomputes Hours, and changing Hours moves End Counter. Only applies
// to types that have all three fields, and only once both counters have a
// value -- otherwise there's nothing to compute from and we'd fight the user
// while they fill the form in.
//
// `prev` / `next` are the flat form states before and after the edit; returns
// the state to actually apply.
export function deriveEntryFields(prev, next, typeId) {
  if (!prev || !next) return next;

  const type = logbookTypeById(typeId);
  const hasField = (key) => type.fields.some((f) => f.key === key);
  if (!hasField('startCounter') || !hasField('endCounter') || !hasField('hours')) {
    return next;
  }

  const number = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const start = number(next.startCounter);
  const end = number(next.endCounter);
  const hours = number(next.hours);
  if (start === null || end === null) return next;

  // Counters carry one or two decimals; drop the float noise (e.g.
  // 2585.8 - 2585 = 0.7999999999999545) so the field shows "0.8".
  const round = (v) => Math.round(v * 1e6) / 1e6;

  if (prev.startCounter !== next.startCounter || prev.endCounter !== next.endCounter) {
    return { ...next, hours: round(end - start) };
  }
  if (prev.hours !== next.hours && hours !== null) {
    return { ...next, endCounter: round(start + hours) };
  }
  return next;
}

// The counters on consecutive entries are one running number: an engine (or an
// airframe) does not move while the boat is tied up, so a new entry's Start
// Counter is almost always the previous entry's End Counter. The entry pages
// OFFER that value on the Start Counter row rather than filling it in -- the
// two really can differ (a counter swap, a run nobody logged), so this must
// stay a suggestion the user accepts.
//
// Ask this rather than testing type ids: eligibility is "the type declares both
// counters", the same shape as supportsRoute's question. Captain's Log and
// Pilot's Logbook qualify today; Maintenance has neither field.
export function supportsCounterCarryForward(id) {
  const fields = logbookTypeById(id).fields;
  return COUNTER_CARRY_KEYS.every((key) => fields.some((f) => f.key === key));
}

const COUNTER_CARRY_KEYS = ['startCounter', 'endCounter'];

// The number to carry forward from `priorEntry`, or null when there isn't one.
//
// Goes through `flattenEntry` rather than reading `entry.endCounter`: on both
// types the counters are `storage: 'json'` and live inside the AWSJSON `fields`
// blob, which arrives as a STRING on a fresh server read. Nothing may reach
// past that.
//
// A genuine 0 is a value, not an absence -- hence Number.isFinite rather than a
// truthiness test, the same guard `deriveEntryFields` uses above.
export function carryForwardEndCounter(priorEntry, typeId) {
  if (!priorEntry || !supportsCounterCarryForward(typeId)) return null;
  const value = flattenEntry(priorEntry, typeId).endCounter;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

// Inverse of `flattenEntry` for saving: split a flat edited object into
// column-backed top-level attributes plus a `fields` object holding the
// json-backed keys. The `fields` blob is always present (possibly empty) so a
// type switch clears it.
//
// ONLY REGISTRY KEYS SURVIVE. `flattenEntry` copies the whole row, so a flat
// object built from a server read carries `owner`, `createdAt`, `updatedAt`
// and the nested `Task` -- none of which exist on
// UpdateTaskPogoCompletedTaskInput. AppSync rejects the whole mutation with
// "the variables input contains a field that is not defined for input object
// type", the autosave lane parks in its error state, and every later edit on
// that page queues behind it. Identity keys (id / itemId / logbookId / taskId)
// are the CALLER's job to re-attach afterwards, which every caller already
// does -- they are not editable fields and must not ride along by accident.
//
// `preserve` is the entry's RAW blob (string | object | null -- `entry.fields`,
// not the entry). Keys in it that this registry doesn't know about are carried
// through to the rebuilt blob. Pass it on every UPDATE.
//
// It is not optional politeness. `flattenEntry` deletes `fields` and this
// function rebuilds it from only the registered keys, and an AWSJSON write
// REPLACES the whole attribute -- so without `preserve` an unregistered key
// (the geo route at `geoRoute`, anything a later feature adds) is silently
// deleted on every save, with no error and no symptom until the next load.
// See the "an unregistered key is destroyed without preserve" test.
//
// Registered keys always win over `preserve`: the flat state is the edit,
// the blob is only what was there before.
export function splitEntryFields(flat, typeId, { preserve } = {}) {
  const type = logbookTypeById(typeId);
  // Json-backed keys, plus any field's `idKey` -- the id binding the entry to
  // a trip (tripGroupId) or a captain to an account (captainUserId), which
  // lives in the blob and has no field entry of its own, the same way
  // `monthsKey` below is a real column with none.
  const jsonKeys = new Set();
  const columnKeys = new Set();
  for (const field of type.fields) {
    if (field.storage === 'json') {
      jsonKeys.add(field.key);
      if (field.idKey) jsonKeys.add(field.idKey);
      continue;
    }
    columnKeys.add(field.key);
    if (field.monthsKey) columnKeys.add(field.monthsKey);
  }

  const out = {};
  const json = {};
  for (const [key, value] of Object.entries(parseFieldsBlob(preserve))) {
    if (!jsonKeys.has(key)) json[key] = value;
  }
  for (const [key, value] of Object.entries(flat || {})) {
    if (jsonKeys.has(key)) {
      json[key] = value;
    } else if (columnKeys.has(key)) {
      out[key] = value;
    }
  }
  out.fields = json;
  return out;
}
